import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack, { StackProps } from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import { mdiCheck } from '@mdi/js';

import { Icon } from 'common/components/Icon';
import { Button } from 'common/components/Button';
import { useOpenCourse } from 'training/hooks/useOpenCourse';
import { CourseAITaskStatusBase } from 'training/components/CourseAITaskStatusBase';

interface IProps extends StackProps {
    taskId: string;
    requestedChapterCount?: number;
    onRetry?: () => void;
}

interface IAICourseResultChapter {
    id: string;
    title: string;
}

interface IAICourseResultData {
    id: string;
    title: string;
    course_data: {
        course_version: {
            content: {
                chapters: IAICourseResultChapter[];
            };
        };
    };
}

const TRANSLATIONS = {
    title: 'AICourseCreateTaskStatus.title',
    progressMessage: 'AICourseCreateTaskStatus.creatingChapters',
    progressFinishingMessage:
        'AICourseCreateTaskStatus.creatingCourseFinishing',
    failedMessage: 'AICourseCreateTaskStatus.creatingCourseFailed',
    finishedMessage: 'AICourseCreateTaskStatus.creatingCourseFinished',
    retryButton: 'AICourseCreateTaskStatus.retry',
};

export const CourseAICreateTaskStatus = ({
    taskId,
    requestedChapterCount = 0,
    onRetry,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const { openCourseEdit, loadingCourseEdit } = useOpenCourse();

    const renderItems = (resultData: IAICourseResultData | undefined) => {
        const { title, course_data } = resultData || {};
        const { chapters = [] } = course_data?.course_version?.content || {};

        return (
            <>
                <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 600 }} variant="h4">
                        {title ? title : <Skeleton />}
                    </Typography>
                </Box>

                {Array.from({ length: requestedChapterCount }).map((_, i) => {
                    const chapter = chapters?.[i];

                    return (
                        <Stack
                            alignItems="center"
                            direction="row"
                            key={chapter?.id ?? i}
                            spacing={1}
                            sx={{ width: '100%' }}
                        >
                            {chapter && (
                                <Icon
                                    path={mdiCheck}
                                    size="1.9rem"
                                    style={{
                                        color: 'var(--mui-palette-success-main)',
                                    }}
                                />
                            )}
                            <Typography sx={{ flex: 1 }} variant="body2">
                                {chapter?.title ? chapter.title : <Skeleton />}
                            </Typography>
                        </Stack>
                    );
                })}
            </>
        );
    };

    const isAllItemsComplete = (
        resultData: IAICourseResultData | undefined
    ) => {
        const chapters =
            resultData?.course_data?.course_version?.content?.chapters || [];

        return chapters.length === requestedChapterCount;
    };

    const renderSuccessAction = (
        resultData: IAICourseResultData | undefined
    ) => {
        const id = resultData?.id;

        if (!id) return null;

        return (
            <Button
                color="primary"
                loading={loadingCourseEdit}
                sx={{ width: '100%' }}
                variant="contained"
                onClick={() => openCourseEdit(id)}
            >
                {translate('viewCourse')}
            </Button>
        );
    };

    return (
        <CourseAITaskStatusBase<IAICourseResultData>
            isAllItemsComplete={isAllItemsComplete}
            renderItems={renderItems}
            renderSuccessAction={renderSuccessAction}
            taskId={taskId}
            translations={TRANSLATIONS}
            onRetry={onRetry}
            {...other}
        />
    );
};
